/**
 * The browser features the client uses, behind small interfaces so tests can replace
 * them. They are typed here rather than through the DOM library because the API
 * imports this package too, and so any app can copy it in unchanged.
 */

interface EventSource {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

const browser = globalThis as Partial<EventSource> & {
  readonly document?: EventSource & { cookie: string };
  readonly location?: { readonly protocol: string };
};

export interface CookieWriteOptions {
  /** The domain to set it on; null for the page's own host only. */
  readonly domain: string | null;
  readonly maxAgeSeconds: number;
  readonly secure: boolean;
}

/** The cookies the page's scripts can see. */
export interface CookieJar {
  read(name: string): string | null;
  write(name: string, value: string, options: CookieWriteOptions): void;
}

/** True on an HTTPS page, where the cookies carry the `__Secure-` prefix. */
export function isSecurePage(): boolean {
  return browser.location?.protocol === 'https:';
}

export function documentCookies(): CookieJar {
  return {
    read(name) {
      for (const pair of (browser.document?.cookie ?? '').split(';')) {
        const separator = pair.indexOf('=');
        if (separator !== -1 && pair.slice(0, separator).trim() === name) {
          return pair.slice(separator + 1).trim();
        }
      }
      return null;
    },
    write(name, value, { domain, maxAgeSeconds, secure }) {
      if (browser.document === undefined) {
        return;
      }
      const attributes = [
        `${name}=${value}`,
        'Path=/',
        `Max-Age=${maxAgeSeconds}`,
        'SameSite=Lax',
        ...(domain === null ? [] : [`Domain=${domain}`]),
        ...(secure ? ['Secure'] : []),
      ];
      browser.document.cookie = attributes.join('; ');
    },
  };
}

/** Calls `listener` whenever the person comes back to the page; returns a function that stops. */
export type OnPageReturn = (listener: () => void) => () => void;

export const onPageReturn: OnPageReturn = (listener) => {
  browser.addEventListener?.('focus', listener);
  browser.document?.addEventListener('visibilitychange', listener);
  return () => {
    browser.removeEventListener?.('focus', listener);
    browser.document?.removeEventListener('visibilitychange', listener);
  };
};
